import { z } from 'zod';
import { SUPPORTED_LOCALES, isSupportedLocale, type SupportedLocale } from '../constants';

// ---------------------------------------------------------------------------
// A hub's localized END-USER presentation (wayai#4948): the name, the welcome message and the
// starter questions a hub shows the people it serves, each per UI locale. Optional, keyed only by
// a supported locale, and stored on the hub row as JSON (`hub.display_names`,
// `hub.welcome_messages`, `hub.starter_questions`, HubDO migration v95). Written by the hub
// create/update bodies and hub-as-code, carried preview → production by publish and sync.
//
// End-user surfaces only: the hub's own `hub_name`, its team and admin views, and the `ai_*`
// notices stay in the hub's single `language`.
// ---------------------------------------------------------------------------

/** The longest localized display name. */
export const HUB_DISPLAY_NAME_MAX_CHARS = 120;
/** The longest localized welcome message. */
export const HUB_WELCOME_MESSAGE_MAX_CHARS = 1000;
/** The most starter questions one locale may offer. */
export const HUB_STARTER_QUESTIONS_MAX = 4;
/** The longest starter question. */
export const HUB_STARTER_QUESTION_MAX_CHARS = 120;

/** A hub's display name per locale; a locale left out shows the resolution's fallback. */
export type HubDisplayNames = Partial<Record<SupportedLocale, string>>;
/** A hub's welcome message per locale. */
export type HubWelcomeMessages = Partial<Record<SupportedLocale, string>>;
/** A hub's starter questions per locale, in the order they are offered. */
export type HubStarterQuestions = Partial<Record<SupportedLocale, string[]>>;

const displayName = z.string().trim().min(1).max(HUB_DISPLAY_NAME_MAX_CHARS);
const welcomeMessage = z.string().trim().min(1).max(HUB_WELCOME_MESSAGE_MAX_CHARS);
const starterQuestions = z
  .array(z.string().trim().min(1).max(HUB_STARTER_QUESTION_MAX_CHARS))
  .min(1)
  .max(HUB_STARTER_QUESTIONS_MAX);

/** An object keyed by the supported locales only, each entry optional; any other key is refused. */
function byLocale<T extends z.ZodTypeAny>(entry: T) {
  const shape = Object.fromEntries(SUPPORTED_LOCALES.map((locale) => [locale, entry.optional()])) as Record<
    SupportedLocale,
    z.ZodOptional<T>
  >;
  return z.object(shape).strict();
}

export const hubDisplayNamesSchema = byLocale(displayName);
export const hubWelcomeMessagesSchema = byLocale(welcomeMessage);
export const hubStarterQuestionsSchema = byLocale(starterQuestions);

/**
 * One of the three as a write takes it (the hub create and update bodies): a map, `null` to clear
 * it, or absent to leave it as stored. An empty map clears it too, so a hub holds either entries
 * or nothing.
 */
function writeField<T extends z.ZodTypeAny>(map: T) {
  return map
    .nullable()
    .optional()
    .transform((value: z.output<T> | null | undefined) =>
      value && Object.keys(value as object).length === 0 ? null : value,
    );
}

export const hubDisplayNamesField = writeField(hubDisplayNamesSchema);
export const hubWelcomeMessagesField = writeField(hubWelcomeMessagesSchema);
export const hubStarterQuestionsField = writeField(hubStarterQuestionsSchema);

/** The three fields, by the name each has on the hub row, the wire and `hub.yaml`. */
export const HUB_PRESENTATION_FIELDS = ['display_names', 'welcome_messages', 'starter_questions'] as const;
export type HubPresentationField = (typeof HUB_PRESENTATION_FIELDS)[number];

/** The three maps, by field: what a wire shape carries (`Partial` of it: each only when set). */
export interface HubPresentationMaps {
  display_names: HubDisplayNames;
  welcome_messages: HubWelcomeMessages;
  starter_questions: HubStarterQuestions;
}

/** The three as the hub row holds them: each map, or null when the hub sets none. */
export type HubPresentationColumns = { [K in HubPresentationField]: HubPresentationMaps[K] | null };

/** Each field's map schema, for a validator that walks all three (hub-as-code). */
export const HUB_PRESENTATION_SCHEMAS = {
  display_names: hubDisplayNamesSchema,
  welcome_messages: hubWelcomeMessagesSchema,
  starter_questions: hubStarterQuestionsSchema,
} as const satisfies Record<HubPresentationField, z.ZodTypeAny>;

/** A stored map as an object: a parsed object, or its JSON text. Anything else is no map. */
function storedObject(raw: unknown): Record<string, unknown> | null {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readByLocale<T>(raw: unknown, entry: z.ZodType<T, z.ZodTypeDef, unknown>): Partial<Record<SupportedLocale, T>> {
  const stored = storedObject(raw);
  const out: Partial<Record<SupportedLocale, T>> = {};
  if (!stored) return out;
  for (const locale of SUPPORTED_LOCALES) {
    const parsed = entry.safeParse(stored[locale]);
    if (parsed.success) out[locale] = parsed.data;
  }
  return out;
}

/**
 * A hub's stored display names (a parsed object, or its JSON text), by locale: only the entries
 * that parse. A locale left out, or stored malformed, has none. The server's hub mappers read the
 * row through these three, so a client never receives an entry a write would refuse.
 */
export function readHubDisplayNames(raw: unknown): HubDisplayNames {
  return readByLocale(raw, displayName);
}

/** A hub's stored welcome messages, by locale: only the entries that parse (see `readHubDisplayNames`). */
export function readHubWelcomeMessages(raw: unknown): HubWelcomeMessages {
  return readByLocale(raw, welcomeMessage);
}

/** A hub's stored starter questions, by locale: only the lists that parse (see `readHubDisplayNames`). */
export function readHubStarterQuestions(raw: unknown): HubStarterQuestions {
  return readByLocale(raw, starterQuestions);
}

const HUB_PRESENTATION_READERS = {
  display_names: readHubDisplayNames,
  welcome_messages: readHubWelcomeMessages,
  starter_questions: readHubStarterQuestions,
} as const satisfies Record<HubPresentationField, (raw: unknown) => object>;

/**
 * A hub's presentation from a hub row (or a `hub.yaml` hub section): each of the three through its
 * reader, present only when it holds an entry — an empty map and no map read alike. Each map lists
 * its locales in `SUPPORTED_LOCALES` order, so two holding the same entries serialize alike. Every
 * server reader of the three goes through this: the hub item mappers, the hub-as-code diff and pull.
 */
export function readHubPresentation(
  row: Readonly<Partial<Record<HubPresentationField, unknown>>>,
): Partial<HubPresentationMaps> {
  const maps: Partial<HubPresentationMaps> = {};
  for (const field of HUB_PRESENTATION_FIELDS) {
    const map = HUB_PRESENTATION_READERS[field](row[field]);
    if (Object.keys(map).length > 0) Object.assign(maps, { [field]: map });
  }
  return maps;
}

// ---------------------------------------------------------------------------
// Resolution — the one rule web and mobile apply, for the viewer's UI locale.
// ---------------------------------------------------------------------------

/** The hub fields the end-user presentation resolves from (a hub item or a hub row). */
export interface HubPresentationSource {
  hub_name?: string | null;
  /** The hub's language (en|pt|es), the first fallback after the viewer's locale. */
  language?: string | null;
  display_names?: HubDisplayNames | null;
  welcome_messages?: HubWelcomeMessages | null;
  starter_questions?: HubStarterQuestions | null;
}

/** What an end-user surface shows for a hub. */
export interface ResolvedHubPresentation {
  /** The hub's name: its display name for the locale, else `hub_name`. */
  name: string;
  /** The welcome message, or null when the hub has none. */
  welcome_message: string | null;
  /** The starter questions, in order; empty when the hub has none. */
  starter_questions: string[];
}

/** A locale tag's language as a supported locale (`pt-BR` → `pt`), or null. */
function supportedLanguageOf(tag: string | null | undefined): SupportedLocale | null {
  if (!tag) return null;
  const language = tag.trim().toLowerCase().split(/[-_]/)[0];
  return isSupportedLocale(language) ? language : null;
}

function nonEmptyText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function nonEmptyQuestions(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const questions = value.filter((q): q is string => nonEmptyText(q) !== null).slice(0, HUB_STARTER_QUESTIONS_MAX);
  return questions.length > 0 ? questions : null;
}

/**
 * The entry a viewer sees from one per-locale map: the viewer's UI locale, then the hub's
 * `language`, then the first locale holding an entry (in `SUPPORTED_LOCALES` order). Null when the
 * map holds no usable entry.
 */
function pickByLocale<T>(
  map: Partial<Record<SupportedLocale, unknown>> | null | undefined,
  usable: (value: unknown) => T | null,
  viewerLocale: string | null | undefined,
  hubLanguage: string | null | undefined,
): T | null {
  if (!map || typeof map !== 'object') return null;
  const order = [supportedLanguageOf(viewerLocale), supportedLanguageOf(hubLanguage), ...SUPPORTED_LOCALES];
  for (const locale of order) {
    if (!locale) continue;
    const value = usable(map[locale]);
    if (value !== null) return value;
  }
  return null;
}

/**
 * The name an end-user surface shows for a hub in `viewerLocale`: its display name by the
 * resolution order (viewer locale → hub `language` → any locale it has), else `hub_name`.
 */
export function resolveHubDisplayName(hub: HubPresentationSource, viewerLocale: string | null | undefined): string {
  return pickByLocale(hub.display_names, nonEmptyText, viewerLocale, hub.language) ?? hub.hub_name ?? '';
}

/**
 * A hub's end-user presentation in `viewerLocale`. Each field resolves on its own, in the order
 * viewer locale → hub `language` → any locale it has: the name falls back to `hub_name`, the
 * welcome message and starter questions to none.
 */
export function resolveHubPresentation(
  hub: HubPresentationSource,
  viewerLocale: string | null | undefined,
): ResolvedHubPresentation {
  return {
    name: resolveHubDisplayName(hub, viewerLocale),
    welcome_message: pickByLocale(hub.welcome_messages, nonEmptyText, viewerLocale, hub.language),
    starter_questions: pickByLocale(hub.starter_questions, nonEmptyQuestions, viewerLocale, hub.language) ?? [],
  };
}
